import { useEffect, useRef, useState } from "react";
import { useReverification, useUser } from "@clerk/react";
import { toast } from "sonner";
import { Camera, Trash2, Loader2, User } from "lucide-react";
import { useAuthedApi, errMsg } from "../../lib/api";
import { useUserLocation } from "../../lib/location";
import { useSite } from "../../lib/site";
import StateCitySelect from "../StateCitySelect";
import NotificationSettings from "../NotificationSettings";

const cErr = (e) => e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message || e?.message || "Something went wrong";
const Section = ({ title, children, testid }) => <div className="lux-card p-5 space-y-4" data-testid={testid}><p className="eyebrow !text-[10px]">{title}</p>{children}</div>;

function PhotoSection({ user, onChanged }) {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const pick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) return toast.error("Choose an image up to 5 MB");
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try { await user.setProfileImage({ file }); await user.reload(); onChanged(); toast.success("Profile photo updated"); }
    catch (e) { toast.error(cErr(e)); setPreview(null); }
    setBusy(false);
  };
  const remove = async () => {
    setBusy(true);
    try { await user.setProfileImage({ file: null }); await user.reload(); onChanged(); setPreview(null); toast.success("Profile photo removed"); }
    catch (e) { toast.error(cErr(e)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-5 flex-wrap">
      <div className="relative w-24 h-24 rounded-full overflow-hidden border-2 border-[#D4AF37] bg-black/40 flex items-center justify-center" data-testid="profile-photo-preview">
        {preview || user.hasImage ? <img src={preview || user.imageUrl} alt="" className="w-full h-full object-cover" /> : <User className="w-10 h-10 text-[#D4AF37]" />}
        {busy && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} data-testid="profile-photo-input" />
        <button type="button" className="btn-gold text-sm" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="profile-photo-upload"><Camera className="w-4 h-4" />{user.hasImage ? "Change photo" : "Upload photo"}</button>
        {user.hasImage && <button type="button" className="btn-outline text-sm !text-red-400 !border-red-500/40" disabled={busy} onClick={remove} data-testid="profile-photo-remove"><Trash2 className="w-4 h-4" />Remove</button>}
      </div>
    </div>
  );
}

function EmailSection({ user, onChanged }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [addr, setAddr] = useState(null);
  const [busy, setBusy] = useState(false);
  const addEmail = useReverification((e) => user.createEmailAddress({ email: e }));
  const makePrimary = useReverification((id) => user.update({ primaryEmailAddressId: id }));
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(cErr(e)); } setBusy(false); };
  const send = () => run(async () => {
    const created = await addEmail(email.trim());
    await user.reload();
    const a = user.emailAddresses.find((x) => x.id === created?.id);
    if (!a) throw new Error("Could not add this email");
    await a.prepareVerification({ strategy: "email_code" });
    setAddr(a); toast.success(`Code sent to ${a.emailAddress}`);
  });
  const verify = () => run(async () => {
    const r = await addr.attemptVerification({ code: code.trim() });
    if (r?.verification?.status !== "verified") throw new Error("Wrong code");
    const old = user.primaryEmailAddress;
    await makePrimary(addr.id);
    if (old && old.id !== addr.id) await old.destroy().catch(() => {});
    await user.reload(); setAddr(null); setEmail(""); setCode(""); toast.success("Email updated"); onChanged();
  });
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-300">Current: <span className="text-white" data-testid="profile-current-email">{user.primaryEmailAddress?.emailAddress || "—"}</span></p>
      {!addr ? (
        <div className="flex flex-wrap gap-2"><input className="lux-input flex-1 min-w-[220px]" type="email" placeholder="new@email.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="profile-new-email" />
          <button type="button" className="btn-outline text-sm" disabled={busy || !/\S+@\S+\.\S+/.test(email)} onClick={send} data-testid="profile-email-send">{busy ? "Sending..." : "Send code"}</button></div>
      ) : (
        <div className="flex flex-wrap gap-2 items-center"><input className="lux-input w-40" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="profile-email-code" />
          <button type="button" className="btn-gold text-sm" disabled={busy || code.length < 6} onClick={verify} data-testid="profile-email-verify">Verify & save</button>
          <button type="button" className="text-xs text-gray-400 underline" onClick={() => setAddr(null)} data-testid="profile-email-cancel">Change email</button></div>
      )}
    </div>
  );
}

function PasswordSection({ user }) {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const update = useReverification((p) => user.updatePassword(p));
  const save = async (e) => {
    e.preventDefault();
    if (f.next.length < 8) return toast.error("New password must be at least 8 characters");
    if (f.next !== f.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try { await update({ ...(user.passwordEnabled ? { currentPassword: f.current } : {}), newPassword: f.next, signOutOfOtherSessions: false }); setF({ current: "", next: "", confirm: "" }); toast.success(user.passwordEnabled ? "Password changed" : "Password set"); }
    catch (err) { toast.error(cErr(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={save} className="grid sm:grid-cols-3 gap-3 items-end">
      {user.passwordEnabled && <div><label className="lux-label">Current password</label><input className="lux-input" type="password" autoComplete="current-password" value={f.current} onChange={(e) => setF({ ...f, current: e.target.value })} data-testid="profile-current-password" /></div>}
      <div><label className="lux-label">New password</label><input className="lux-input" type="password" autoComplete="new-password" value={f.next} onChange={(e) => setF({ ...f, next: e.target.value })} data-testid="profile-new-password" /></div>
      <div><label className="lux-label">Confirm new password</label><input className="lux-input" type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="profile-confirm-password" /></div>
      <button className="btn-outline text-sm sm:col-span-3 justify-self-start" disabled={busy || !f.next} data-testid="profile-password-save">{busy ? "Saving..." : user.passwordEnabled ? "Change password" : "Set password"}</button>
    </form>
  );
}

export default function ProfileEditor({ me, onSaved }) {
  const api = useAuthedApi();
  const { user, isLoaded } = useUser();
  const { setMe } = useSite();
  const { location, setLocation } = useUserLocation();
  const [f, setF] = useState({ name: me.name || "", phone: me.phone || "" });
  const [loc, setLoc] = useState({ state: location?.state || "", city: location?.city || "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLoc({ state: location?.state || "", city: location?.city || "" }); }, [location]);

  const sync = async (body = f) => { const r = await api("put", "/me", body); onSaved({ ...me, ...r }); setMe((p) => ({ ...p, ...r })); return r; };
  const save = async (e) => {
    e.preventDefault();
    if (f.phone && !/^\+?[0-9][0-9 -]{8,14}$/.test(f.phone)) return toast.error("Enter a valid mobile number");
    setBusy(true);
    try {
      const [first, ...rest] = f.name.trim().split(/\s+/);
      await user?.update({ firstName: first, lastName: rest.join(" ") }).catch(() => {});
      await sync({ ...f, ...(loc.city ? loc : {}) });
      if (loc.state !== (location?.state || "") || loc.city !== (location?.city || "")) setLocation(loc);
      toast.success("Profile updated");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  if (!isLoaded || !user) return <p className="text-gray-500">Loading...</p>;
  return (
    <div className="space-y-5 max-w-3xl" data-testid="profile-editor">
      <Section title="Profile photo" testid="profile-photo-section"><PhotoSection user={user} onChanged={() => sync().catch(() => {})} /></Section>
      <form onSubmit={save} data-testid="profile-form">
        <Section title="Your details">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="lux-label">Full name</label><input className="lux-input" required maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name" /></div>
            <div><label className="lux-label">Mobile number</label><input className="lux-input" inputMode="tel" maxLength={15} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="98XXXXXXXX" data-testid="profile-phone" /></div>
            <StateCitySelect state={loc.state} city={loc.city} prefix="profile-" onChange={(v) => setLoc({ ...loc, ...v })} />
          </div>
          <p className="text-xs text-gray-500">Your name and number pre-fill new listings, offers and enquiries. Your city also sets the location in the top bar.</p>
          <button className="btn-gold" disabled={busy} data-testid="profile-save">{busy ? "Saving..." : "Save profile"}</button>
        </Section>
      </form>
      <Section title="Email address" testid="profile-email-section"><EmailSection user={user} onChanged={() => sync().catch(() => {})} /></Section>
      <Section title="Password" testid="profile-password-section"><PasswordSection user={user} /></Section>
      <Section title="Notifications" testid="profile-notifications-section"><NotificationSettings /></Section>
    </div>
  );
}
